import { z } from "zod";

/** Content-addressed blob id: sha256 hex of the bytes (ARCHITECTURE §5.1). */
export const BlobIdSchema = z.string().regex(/^[0-9a-f]{64}$/, "blobId must be a sha256 hex digest");
export type BlobId = z.infer<typeof BlobIdSchema>;

export const MimeTypeSchema = z.string().regex(/^[\w.+-]+\/[\w.+-]+$/, "mimeType must look like type/subtype");

/**
 * The one representation for content. Text and images in a single list; an
 * image is a reference, never inlined bytes.
 */
export const PartSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("text"), text: z.string() }).strict(),
  z.object({ type: z.literal("image"), blobId: BlobIdSchema, mimeType: MimeTypeSchema }).strict(),
]);
export type Part = z.infer<typeof PartSchema>;

export const AttachmentSchema = z.object({ blobId: BlobIdSchema, mimeType: MimeTypeSchema }).strict();
export type Attachment = z.infer<typeof AttachmentSchema>;

export const ToolResultSchema = z.object({
  parts: z.array(PartSchema),
  isError: z.boolean().optional(),
}).strict();
export type ToolResult = z.infer<typeof ToolResultSchema>;

/**
 * What a transcript entry may hold. `Part` (above) is what tools return and
 * what the UI shows; an entry additionally records the model's reasoning
 * (with the provider options needed to replay it), the calls it made, and the
 * results those calls produced (ARCHITECTURE §6.5).
 */
export const EntryPartSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("text"), text: z.string() }).strict(),
  z.object({ type: z.literal("image"), blobId: BlobIdSchema, mimeType: MimeTypeSchema }).strict(),
  z.object({ type: z.literal("reasoning"), text: z.string(), providerOptions: z.record(z.string(), z.unknown()).optional() }).strict(),
  z.object({ type: z.literal("tool_call"), callId: z.string().min(1), name: z.string().min(1), args: z.unknown() }).strict(),
  z.object({ type: z.literal("tool_result"), callId: z.string().min(1), name: z.string().min(1), result: ToolResultSchema }).strict(),
]);
export type EntryPart = z.infer<typeof EntryPartSchema>;
